/**
 * DataReferencePickerDialog Component
 *
 * Dialog for selecting data references using CompositeDataPicker.
 * Replaces legacy DataReferenceTree dialog in RichMentionEditor.
 *
 * Features:
 * - Uses CompositeDataPicker for autonomous data loading
 * - Returns both paths (for mentions) and hydrated data (for AI context)
 * - Apply/Cancel actions with proper state management
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { CompositeDataPicker } from '@modules/data-pickers';
import type { HydratedData } from '@modules/data-pickers/components/CompositeDataPicker/types';
import type {
  DataReferencePickerDialogProps,
  DataReferenceSelectionResult,
} from './types';

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * DataReferencePickerDialog Component
 *
 * @param props - Component props
 * @returns React component or null if closed
 */
export function DataReferencePickerDialog(
  props: DataReferencePickerDialogProps
): React.ReactElement | null {
  const {
    open,
    onClose,
    onApply,
    projectId,
    initialSelection = [],
    title = 'Select Data References',
    pickers,
    allowEmptyApply = false,
    height = 400,
    maxWidth = 'sm',
    'data-testid': dataTestId,
  } = props;

  // ===========================================================================
  // STATE
  // ===========================================================================

  const [selectedPaths, setSelectedPaths] = useState<string[]>(initialSelection);
  const [hydratedData, setHydratedData] = useState<HydratedData>({
    audiences: [],
    tasks: [],
  });

  // ===========================================================================
  // EFFECTS
  // ===========================================================================

  /**
   * Reset selection when dialog opens
   * Note: We only track `open` to avoid infinite loops from prop reference changes
   */
  useEffect(() => {
    if (open) {
      // Reset to initial selection when dialog opens
      setSelectedPaths(initialSelection);
      setHydratedData({ audiences: [], tasks: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // Intentionally omit initialSelection to prevent infinite loops

  // ===========================================================================
  // HANDLERS
  // ===========================================================================

  /**
   * Handle selection change from CompositeDataPicker
   */
  const handleSelectionChange = useCallback((paths: string[]) => {
    setSelectedPaths(paths);
  }, []);

  /**
   * Handle hydrated data change from CompositeDataPicker
   */
  const handleHydratedDataChange = useCallback((data: HydratedData) => {
    setHydratedData(data);
  }, []);

  /**
   * Handle Apply button click
   * Calls onApply with result, then closes dialog
   */
  const handleApply = useCallback(() => {
    const result: DataReferenceSelectionResult = {
      paths: selectedPaths,
      hydratedData,
    };
    onApply(result);
    onClose();
  }, [selectedPaths, hydratedData, onApply, onClose]);

  /**
   * Handle Cancel/Done button or close actions
   * Closes dialog without applying selection
   */
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // ===========================================================================
  // RENDER
  // ===========================================================================

  if (!open) return null;

  const hasSelection = selectedPaths.length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth={maxWidth}
      fullWidth
      data-testid={dataTestId}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.15)',
            backdropFilter: 'blur(20px)',
          },
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        {title}
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        <CompositeDataPicker
          projectId={projectId}
          savedSelections={selectedPaths}
          onSelectionChange={handleSelectionChange}
          onHydratedDataChange={handleHydratedDataChange}
          pickers={pickers ?? [
            { type: 'audience', label: 'Audiences', enabled: true },
            { type: 'task', label: 'Tasks', enabled: true },
          ]}
          layout="tabs"
          height={height}
        />
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1 }}>
        <Button
          onClick={handleApply}
          variant="contained"
          color="primary"
          disabled={!allowEmptyApply && !hasSelection}
          data-testid={dataTestId ? `${dataTestId}-apply` : undefined}
        >
          Apply Selection
        </Button>
        <Button onClick={handleCancel} color="inherit">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DataReferencePickerDialog;
